import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

const PROPERTY_TYPES = ['apartment', 'villa', 'townhouse', 'office'];
const STATUSES = ['off-plan', 'ready', 'rented', 'vacant'];
const FILE_TYPES = ['brochure', 'contract', 'receipt', 'floor plan', 'other'];

const emptyForm = {
  name: '', developer: '', location: '', city: '', country: 'UAE',
  property_type: 'apartment', status: 'ready', purchase_price: '', current_value: '',
  currency: 'AED', monthly_rent: '', expected_rent_yearly: '', rental_yield: '',
  service_charges_yearly: '', service_charges_currency: 'AED',
  expected_delivery_date: '',
  dld_type: 'none', dld_purchase_fee: '', dld_handover_fee: '', admin_fees: '',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const money = (n, c = 'AED') => `${c} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const toISO = (v) => v ? `${v}T00:00:00` : null;
const dateInput = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

const statusBadge = {
  'off-plan': 'bg-purple-50 text-purple-700',
  'ready': 'bg-blue-50 text-blue-700',
  'rented': 'bg-green-50 text-green-700',
  'vacant': 'bg-amber-50 text-amber-700',
};

const typeIcon = { apartment: '🏢', villa: '🏡', townhouse: '🏘️', office: '🏬' };

export default function Properties({ token, onLogout }) {
  const [properties, setProperties] = useState([]);
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedId, setSelectedId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = () =>
    axios.get(`${API}/properties`, { headers }).then(r => setProperties(r.data));

  useEffect(() => {
    load();
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
  }, []);

  const selected = properties.find(p => p.id === selectedId) || null;

  // ---- Property CRUD ----
  const openAdd = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || '', developer: p.developer || '', location: p.location || '',
      city: p.city || '', country: p.country || '', property_type: p.property_type || 'apartment',
      status: p.status || 'ready', purchase_price: p.purchase_price ?? '', current_value: p.current_value ?? '',
      currency: p.currency || 'AED', monthly_rent: p.monthly_rent ?? '', expected_rent_yearly: p.expected_rent_yearly ?? '',
      rental_yield: p.rental_yield ?? '', service_charges_yearly: p.service_charges_yearly ?? '',
      service_charges_currency: p.service_charges_currency || 'AED',
      expected_delivery_date: dateInput(p.expected_delivery_date),
      dld_type: p.dld_type || 'none', dld_purchase_fee: p.dld_purchase_fee ?? '',
      dld_handover_fee: p.dld_handover_fee ?? '', admin_fees: p.admin_fees ?? '',
    });
    setShowForm(true);
  };

  const saveProperty = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      purchase_price: parseFloat(form.purchase_price) || 0,
      current_value: parseFloat(form.current_value) || 0,
      monthly_rent: parseFloat(form.monthly_rent) || 0,
      expected_rent_yearly: parseFloat(form.expected_rent_yearly) || 0,
      rental_yield: parseFloat(form.rental_yield) || 0,
      service_charges_yearly: parseFloat(form.service_charges_yearly) || 0,
      service_charges_currency: form.service_charges_currency || 'AED',
      expected_delivery_date: toISO(form.expected_delivery_date),
      dld_type: form.dld_type || 'none',
      dld_purchase_fee: parseFloat(form.dld_purchase_fee) || 0,
      dld_handover_fee: parseFloat(form.dld_handover_fee) || 0,
      admin_fees: parseFloat(form.admin_fees) || 0,
    };
    if (editingId) await axios.put(`${API}/properties/${editingId}`, payload, { headers });
    else await axios.post(`${API}/properties`, payload, { headers });
    setShowForm(false); setForm(emptyForm); setEditingId(null);
    await load();
  };

  const deleteProperty = async (id) => {
    if (!window.confirm('Delete this property and all its payments & files?')) return;
    await axios.delete(`${API}/properties/${id}`, { headers });
    if (selectedId === id) setSelectedId(null);
    await load();
  };

  const quickUpdateValue = async (p) => {
    const v = window.prompt(`Update current estimated value (${p.currency}):`, p.current_value);
    if (v === null) return;
    const num = parseFloat(v);
    if (isNaN(num)) return;
    await axios.put(`${API}/properties/${p.id}`, {
      name: p.name, developer: p.developer, location: p.location, city: p.city, country: p.country,
      property_type: p.property_type, status: p.status, purchase_price: p.purchase_price,
      current_value: num, currency: p.currency, monthly_rent: p.monthly_rent,
      expected_rent_yearly: p.expected_rent_yearly, rental_yield: p.rental_yield,
      service_charges_yearly: p.service_charges_yearly, service_charges_currency: p.service_charges_currency,
      expected_delivery_date: p.expected_delivery_date,
      dld_type: p.dld_type, dld_purchase_fee: p.dld_purchase_fee,
      dld_handover_fee: p.dld_handover_fee, admin_fees: p.admin_fees,
    }, { headers });
    await load();
  };

  // ---- Portfolio summary ----
  const totalValue = properties.reduce((s, p) => s + (p.current_value || 0), 0);
  const totalCost = properties.reduce((s, p) => s + (p.total_investment ?? p.purchase_price ?? 0), 0);
  const totalAppreciation = totalValue - totalCost;
  const apprPct = totalCost > 0 ? ((totalAppreciation / totalCost) * 100).toFixed(1) : 0;
  const monthlyRent = properties.reduce((s, p) => s + (p.status === 'rented' || p.status === 'ready' ? (p.monthly_rent || 0) : 0), 0);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Real Estate</h1>
            <p className="text-gray-500 text-sm mt-1">
              {properties.length} {properties.length === 1 ? 'property' : 'properties'} in portfolio
            </p>
          </div>
          <button onClick={openAdd}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap">
            + Add Property
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <SummaryCard label="Portfolio Value" value={money(totalValue)} accent="text-gray-900" />
          <SummaryCard label="Total Invested" value={money(totalCost)} accent="text-gray-900" />
          <SummaryCard label="Appreciation"
            value={`${totalAppreciation >= 0 ? '+' : ''}${money(totalAppreciation)}`}
            sub={`${apprPct}%`} accent={totalAppreciation >= 0 ? 'text-green-600' : 'text-red-600'} />
          <SummaryCard label="Monthly Rent" value={money(monthlyRent)} accent="text-gray-900" />
        </div>

        {/* Add/Edit form */}
        {showForm && (
          <PropertyForm form={form} setForm={setForm} onSubmit={saveProperty}
            onCancel={() => { setShowForm(false); setEditingId(null); }} editing={!!editingId} />
        )}

        {/* Property grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {properties.length === 0 && !showForm && (
            <div className="col-span-full bg-white rounded-2xl p-12 text-center text-gray-400 text-sm border border-gray-100">
              No properties yet. Add your first one!
            </div>
          )}
          {properties.map(p => (
            <PropertyCard key={p.id} p={p}
              onOpen={() => setSelectedId(p.id)} onEdit={() => openEdit(p)}
              onDelete={() => deleteProperty(p.id)} onUpdateValue={() => quickUpdateValue(p)} />
          ))}
        </div>

        {/* Detail drawer */}
        {selected && (
          <PropertyDetail p={selected} headers={headers} onClose={() => setSelectedId(null)}
            onChanged={load} onEdit={() => { setSelectedId(null); openEdit(selected); }} />
        )}
      </main>
    </div>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`font-bold text-base md:text-lg ${accent}`}>{value}</p>
      {sub && <p className={`text-xs font-medium ${accent}`}>{sub}</p>}
    </div>
  );
}

function PropertyCard({ p, onOpen, onEdit, onDelete, onUpdateValue }) {
  const gainPos = (p.appreciation || 0) >= 0;
  const isOffPlan = p.status === 'off-plan';
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{typeIcon[p.property_type] || '🏠'} {p.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5 truncate">
            📍 {[p.location, p.city, p.country].filter(Boolean).join(', ') || '—'}
          </p>
          {p.developer && <p className="text-xs text-gray-400 mt-0.5 truncate">by {p.developer}</p>}
        </div>
        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${statusBadge[p.status] || 'bg-gray-100 text-gray-600'}`}>
          {p.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-500 mb-0.5">Current value</p>
          <p className="font-semibold text-gray-900 text-sm">{money(p.current_value, p.currency)}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-500 mb-0.5">Gain / loss</p>
          <p className={`font-semibold text-sm ${gainPos ? 'text-green-600' : 'text-red-600'}`}>
            {gainPos ? '+' : ''}{money(p.appreciation, p.currency)} ({p.appreciation_pct}%)
          </p>
        </div>
      </div>

      {/* Payment plan progress (off-plan) */}
      {isOffPlan && p.plan_total > 0 && (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Payment plan</span>
            <span>{p.plan_progress_pct}% paid</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-green-500" style={{ width: `${Math.min(p.plan_progress_pct, 100)}%` }} />
          </div>
          {p.next_payment && (
            <p className="text-xs text-gray-500 mt-1.5">
              Next: <span className="font-medium text-gray-700">{money(p.next_payment.amount, p.next_payment.currency)}</span> on {fmtDate(p.next_payment.due_date)}
            </p>
          )}
        </div>
      )}

      {/* Rent / yield */}
      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
        {(p.status === 'ready' || p.status === 'rented') && p.monthly_rent > 0 && (
          <span>💰 {money(p.monthly_rent, p.currency)}/mo</span>
        )}
        {isOffPlan && p.expected_rent_yearly > 0 && (
          <span>💰 ~{money(p.expected_rent_yearly / 12, p.currency)}/mo exp.</span>
        )}
        {p.rental_yield > 0 && <span>📈 {p.rental_yield}% yield</span>}
        {p.files?.length > 0 && <span>📎 {p.files.length}</span>}
      </div>

      <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100">
        <button onClick={onOpen} className="flex-1 bg-gray-900 text-white text-xs py-2 rounded-lg font-medium">Manage</button>
        <button onClick={onUpdateValue} className="px-3 bg-gray-100 text-gray-700 text-xs py-2 rounded-lg" title="Update value">↻</button>
        <button onClick={onEdit} className="px-3 bg-gray-100 text-gray-700 text-xs py-2 rounded-lg">Edit</button>
        <button onClick={onDelete} className="px-3 bg-red-50 text-red-600 text-xs py-2 rounded-lg">✕</button>
      </div>
    </div>
  );
}

function PropertyForm({ form, setForm, onSubmit, onCancel, editing }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 w-full";
  const label = "text-xs text-gray-500 mb-1 block";

  // ---- Transaction fees (Dubai DLD on purchase / RAK DLD on handover) ----
  const purchasePrice = parseFloat(form.purchase_price) || 0;
  const suggested = Math.round(purchasePrice * 0.04);
  const toggleDld = (type) => {
    if (form.dld_type === type) {
      setForm({ ...form, dld_type: 'none' });
    } else if (type === 'dubai') {
      setForm({ ...form, dld_type: 'dubai', dld_purchase_fee: suggested || '', dld_handover_fee: '' });
    } else {
      setForm({ ...form, dld_type: 'rak', dld_handover_fee: suggested || '', dld_purchase_fee: '' });
    }
  };
  const feeMoney = (n) => `AED ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const dldFee = form.dld_type === 'dubai' ? form.dld_purchase_fee
    : form.dld_type === 'rak' ? form.dld_handover_fee : 0;
  const totalInvestment = purchasePrice + (parseFloat(dldFee) || 0) + (parseFloat(form.admin_fees) || 0);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
      <h2 className="text-base font-semibold mb-4">{editing ? 'Edit Property' : 'New Property'}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div><label className={label}>Name / Project *</label>
          <input required value={form.name} onChange={set('name')} className={input} placeholder="Marina Vista 2BR" /></div>
        <div><label className={label}>Developer</label>
          <input value={form.developer} onChange={set('developer')} className={input} placeholder="Emaar" /></div>
        <div><label className={label}>Area / Location</label>
          <input value={form.location} onChange={set('location')} className={input} placeholder="Dubai Marina" /></div>
        <div><label className={label}>City</label>
          <input value={form.city} onChange={set('city')} className={input} placeholder="Dubai" /></div>
        <div><label className={label}>Country</label>
          <input value={form.country} onChange={set('country')} className={input} placeholder="UAE" /></div>
        <div><label className={label}>Type</label>
          <select value={form.property_type} onChange={set('property_type')} className={input}>
            {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label className={label}>Status</label>
          <select value={form.status} onChange={set('status')} className={input}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div><label className={label}>Currency</label>
          <select value={form.currency} onChange={set('currency')} className={input}>
            <option>AED</option><option>USD</option>
          </select></div>
        <div><label className={label}>Purchase price</label>
          <input type="number" value={form.purchase_price} onChange={set('purchase_price')} className={input} placeholder="1800000" /></div>
        <div><label className={label}>Current value</label>
          <input type="number" value={form.current_value} onChange={set('current_value')} className={input} placeholder="2100000" /></div>
        <div><label className={label}>Actual monthly rent (ready)</label>
          <input type="number" value={form.monthly_rent} onChange={set('monthly_rent')} className={input} placeholder="12000" /></div>
        <div><label className={label}>Expected annual rent (off-plan)</label>
          <input type="number" value={form.expected_rent_yearly} onChange={set('expected_rent_yearly')} className={input} placeholder="156000" />
          {parseFloat(form.expected_rent_yearly) > 0 && (
            <p className="text-xs text-gray-400 mt-1">≈ {feeMoney(parseFloat(form.expected_rent_yearly) / 12)}/mo</p>
          )}
        </div>
        <div><label className={label}>Annual service charges</label>
          <div className="flex gap-2">
            <input type="number" value={form.service_charges_yearly} onChange={set('service_charges_yearly')} className={input} placeholder="15000" />
            <select value={form.service_charges_currency} onChange={set('service_charges_currency')} className={input + ' w-20'}>
              <option>AED</option><option>USD</option>
            </select>
          </div>
        </div>
        <div><label className={label}>Expected yield %</label>
          <input type="number" step="0.1" value={form.rental_yield} onChange={set('rental_yield')} className={input} placeholder="7.5" /></div>
        <div><label className={label}>Expected delivery (off-plan)</label>
          <input type="date" value={form.expected_delivery_date} onChange={set('expected_delivery_date')} className={input} /></div>

        {/* Transaction fees */}
        <div className="md:col-span-2 lg:col-span-3 border-t border-gray-100 pt-4 mt-1">
          <p className="text-sm font-semibold text-gray-700 mb-1">Transaction Fees</p>
          <p className="text-xs text-gray-400 mb-3">Select the DLD fee that applies based on the property location. Auto-calculated at 4% of purchase price — you can override the amount.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Dubai — DLD on purchase */}
            <div className="bg-gray-50 rounded-xl p-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-2">
                <input type="checkbox" checked={form.dld_type === 'dubai'} onChange={() => toggleDld('dubai')} />
                DLD Fee on Purchase <span className="text-xs text-gray-400">(Dubai · 4%)</span>
              </label>
              {form.dld_type === 'dubai' && (
                <div className="flex gap-2 items-center">
                  <input type="number" value={form.dld_purchase_fee} onChange={set('dld_purchase_fee')} className={input} placeholder={suggested || '72000'} />
                  <button type="button" onClick={() => setForm({ ...form, dld_purchase_fee: suggested || '' })}
                    className="text-xs whitespace-nowrap bg-gray-200 text-gray-700 px-2 py-2 rounded-lg" title="Recalculate 4% of purchase price">↻ 4%</button>
                </div>
              )}
            </div>
            {/* RAK — DLD on handover */}
            <div className="bg-gray-50 rounded-xl p-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-2">
                <input type="checkbox" checked={form.dld_type === 'rak'} onChange={() => toggleDld('rak')} />
                DLD Fee on Handover <span className="text-xs text-gray-400">(RAK · 4%)</span>
              </label>
              {form.dld_type === 'rak' && (
                <div className="flex gap-2 items-center">
                  <input type="number" value={form.dld_handover_fee} onChange={set('dld_handover_fee')} className={input} placeholder={suggested || '72000'} />
                  <button type="button" onClick={() => setForm({ ...form, dld_handover_fee: suggested || '' })}
                    className="text-xs whitespace-nowrap bg-gray-200 text-gray-700 px-2 py-2 rounded-lg" title="Recalculate 4% of purchase price">↻ 4%</button>
                </div>
              )}
            </div>
            {/* Admin fees */}
            <div className="bg-gray-50 rounded-xl p-3">
              <label className={label + ' !text-gray-700 !text-sm'}>Admin Fees <span className="text-xs text-gray-400">(manual)</span></label>
              <input type="number" value={form.admin_fees} onChange={set('admin_fees')} className={input} placeholder="5000" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
            <span>DLD fee: <span className="font-medium text-gray-800">{feeMoney(dldFee)}</span></span>
            <span>Admin fees: <span className="font-medium text-gray-800">{feeMoney(form.admin_fees)}</span></span>
            <span>Total investment: <span className="font-semibold text-gray-900">{feeMoney(totalInvestment)}</span></span>
          </div>
        </div>

        <div className="flex gap-3 items-end md:col-span-2 lg:col-span-1">
          <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">Save</button>
          <button type="button" onClick={onCancel} className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function PropertyDetail({ p, headers, onClose, onChanged, onEdit }) {
  const [tab, setTab] = useState('plan');
  const isOffPlan = p.status === 'off-plan';
  const tabs = [
    { k: 'plan', label: 'Payment Plan' },
    { k: 'income', label: 'Income & ROI' },
    { k: 'appr', label: 'Appreciation' },
    { k: 'files', label: `Files (${p.files?.length || 0})` },
  ];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-gray-50 w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">{typeIcon[p.property_type] || '🏠'} {p.name}</h2>
            <p className="text-sm text-gray-500 truncate">{[p.location, p.city, p.country].filter(Boolean).join(', ')}</p>
            <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${statusBadge[p.status] || 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onEdit} className="text-xs bg-gray-100 text-gray-700 px-3 py-2 rounded-lg">Edit</button>
            <button onClick={onClose} className="text-xs bg-gray-100 text-gray-700 px-3 py-2 rounded-lg">Close</button>
          </div>
        </div>

        {/* tabs */}
        <div className="flex gap-1 px-5 pt-4 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`text-sm px-3 py-2 rounded-lg whitespace-nowrap ${tab === t.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === 'plan' && <PaymentPlan p={p} headers={headers} onChanged={onChanged} />}
          {tab === 'income' && <IncomeSection p={p} isOffPlan={isOffPlan} />}
          {tab === 'appr' && <AppreciationSection p={p} />}
          {tab === 'files' && <FilesSection p={p} headers={headers} onChanged={onChanged} />}
        </div>
      </div>
    </div>
  );
}

function daysUntilDue(pay) {
  if (!pay.due_date) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(pay.due_date); due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / (1000 * 60 * 60 * 24));
}

function dueLabel(pay) {
  if (pay.paid) return '';
  const days = daysUntilDue(pay);
  if (days === null) return '';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `in ${days} days`;
}

function paymentStatus(pay) {
  if (pay.paid) return { color: 'green', label: 'Paid' };
  const days = daysUntilDue(pay);
  if (days === null) return { color: 'orange', label: 'Upcoming' };
  if (days < 0) return { color: 'red', label: 'Overdue' };
  if (days <= 30) return { color: 'orange', label: 'Upcoming' };
  return { color: 'gray', label: 'Future' };
}
const dot = { green: 'bg-green-500', red: 'bg-red-500', orange: 'bg-amber-500', gray: 'bg-gray-400' };
const chip = {
  green: 'bg-green-50 text-green-700', red: 'bg-red-50 text-red-700',
  orange: 'bg-amber-50 text-amber-700', gray: 'bg-gray-100 text-gray-600',
};

function PaymentPlan({ p, headers, onChanged }) {
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ label: '', amount: '', due_date: '', currency: p.currency, add_to_recurring_bills: false });

  const add = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/properties/${p.id}/payments`, {
      label: f.label, amount: parseFloat(f.amount) || 0, currency: f.currency,
      due_date: toISO(f.due_date), add_to_recurring_bills: f.add_to_recurring_bills,
    }, { headers });
    setF({ label: '', amount: '', due_date: '', currency: p.currency, add_to_recurring_bills: false });
    setShow(false);
    await onChanged();
  };
  const toggle = async (pay) => {
    await axios.put(`${API}/properties/${p.id}/payments/${pay.id}`, { paid: !pay.paid }, { headers });
    await onChanged();
  };
  const remove = async (pay) => {
    if (!window.confirm('Delete this installment?')) return;
    await axios.delete(`${API}/properties/${p.id}/payments/${pay.id}`, { headers });
    await onChanged();
  };
  const input = "border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 w-full";

  return (
    <div>
      {/* totals */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-white rounded-xl p-3 border border-gray-100">
          <p className="text-xs text-gray-500">Plan total</p>
          <p className="font-semibold text-sm">{money(p.plan_total, p.currency)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-100">
          <p className="text-xs text-gray-500">Paid</p>
          <p className="font-semibold text-sm text-green-600">{money(p.plan_paid, p.currency)}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-gray-100">
          <p className="text-xs text-gray-500">Remaining</p>
          <p className="font-semibold text-sm text-amber-600">{money(p.plan_remaining, p.currency)}</p>
        </div>
      </div>
      {p.plan_total > 0 && (
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-green-500" style={{ width: `${Math.min(p.plan_progress_pct, 100)}%` }} />
        </div>
      )}

      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Installments</h3>
        <button onClick={() => setShow(!show)} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg">+ Installment</button>
      </div>

      {show && (
        <form onSubmit={add} className="bg-white rounded-xl p-3 border border-gray-100 mb-3 grid grid-cols-3 gap-2">
          <input className={input + ' col-span-3'} placeholder="Name / description (e.g. 20% Down Payment)" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} />
          <input className={input} type="number" placeholder="Amount" required value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
          <select className={input} value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
            <option>AED</option><option>USD</option>
          </select>
          <input className={input} type="date" required value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} />
          <label className="col-span-3 flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={f.add_to_recurring_bills} onChange={e => setF({ ...f, add_to_recurring_bills: e.target.checked })} />
            Also add to Recurring Bills (as a reminder)
          </label>
          <div className="col-span-3 flex gap-2">
            <button type="submit" className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-xs">Save</button>
            <button type="button" onClick={() => setShow(false)} className="border border-gray-200 px-4 py-1.5 rounded-lg text-xs">Cancel</button>
          </div>
        </form>
      )}

      {(!p.payments || p.payments.length === 0) ? (
        <p className="text-center text-gray-400 text-sm py-6">No installments yet.</p>
      ) : (
        <div className="space-y-2">
          {p.payments.map(pay => {
            const st = paymentStatus(pay);
            return (
              <div key={pay.id} className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full ${dot[st.color]} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{pay.label || 'Installment'}</p>
                  <p className="text-xs text-gray-500">Due {fmtDate(pay.due_date)}{!pay.paid && dueLabel(pay) ? ` · ${dueLabel(pay)}` : ''}{pay.paid && pay.paid_date ? ` · paid ${fmtDate(pay.paid_date)}` : ''}{pay.recurring_bill_id ? ' · 🔔 bill' : ''}{pay.expense_id ? ' · 🧾 expense' : ''}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold">{money(pay.amount, pay.currency)}</p>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${chip[st.color]}`}>{st.label}</span>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => toggle(pay)} className="text-xs bg-gray-100 px-2 py-1 rounded" title="Toggle paid">{pay.paid ? '↺' : '✓'}</button>
                  <button onClick={() => remove(pay)} className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded">✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RoiCard({ label, value, formula }) {
  const pos = (value || 0) >= 0;
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${pos ? 'text-green-600' : 'text-red-600'}`}>
        {pos ? '' : ''}{Number(value || 0).toFixed(2)}%
      </p>
      {formula && <p className="text-xs text-gray-400 mt-1 leading-snug">{formula}</p>}
    </div>
  );
}

function IncomeSection({ p, isOffPlan }) {
  const cur = p.currency || 'AED';
  const rentYearly = p.expected_rent_yearly || 0;
  const rentMonthly = rentYearly / 12;
  const serviceYearly = p.service_charges_yearly || 0;
  const serviceCur = p.service_charges_currency || 'AED';
  const ti = p.total_investment ?? p.purchase_price ?? 0;
  // ROI metrics (prefer backend-computed canonical values, fall back to local calc)
  const grossYield = p.gross_rental_yield ?? (ti > 0 ? (rentYearly / ti) * 100 : 0);
  const netYield = p.net_rental_yield ?? (ti > 0 ? ((rentYearly - serviceYearly) / ti) * 100 : 0);
  const capRoi = p.capital_appreciation_roi ?? (ti > 0 ? (((p.current_value || 0) - ti) / ti) * 100 : 0);
  const totalRoi = p.total_roi ?? (grossYield + capRoi);
  const fmt = (n) => money(n, cur);
  return (
    <div className="space-y-3">
      {/* Rent */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <p className="text-xs text-gray-500 mb-1">Expected annual rent</p>
        <p className="text-xl font-bold text-gray-900">{fmt(rentYearly)}<span className="text-sm font-normal text-gray-400"> /yr</span></p>
        <p className="text-sm text-gray-500 mt-0.5">≈ {fmt(rentMonthly)} /mo</p>
      </div>

      {/* Service charges */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Annual service charges</p>
          <p className="font-semibold">{money(serviceYearly, serviceCur)}<span className="text-xs font-normal text-gray-400"> /yr</span></p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Net annual rent (after charges)</p>
          <p className="font-semibold">{fmt(rentYearly - serviceYearly)}</p>
        </div>
      </div>

      {/* ROI */}
      <div className="pt-1">
        <p className="text-sm font-semibold text-gray-700 mb-1">Return on Investment</p>
        <p className="text-xs text-gray-400 mb-3">All ratios are calculated against total investment of <span className="font-medium text-gray-600">{fmt(ti)}</span> (purchase price + DLD fees + admin fees).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <RoiCard label="Gross rental yield" value={grossYield}
            formula={`${fmt(rentYearly)} ÷ ${fmt(ti)} × 100`} />
          <RoiCard label="Net rental yield (after charges)" value={netYield}
            formula={`(${fmt(rentYearly)} − ${money(serviceYearly, serviceCur)}) ÷ ${fmt(ti)} × 100`} />
          <RoiCard label="Capital appreciation ROI" value={capRoi}
            formula={`(${fmt(p.current_value)} − ${fmt(ti)}) ÷ ${fmt(ti)} × 100`} />
          <RoiCard label="Total ROI (rental + appreciation)" value={totalRoi}
            formula={`${Number(grossYield).toFixed(2)}% gross yield + ${Number(capRoi).toFixed(2)}% appreciation`} />
        </div>
      </div>

      {p.rental_yield > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Manually entered expected yield</p>
          <p className="font-semibold">{p.rental_yield}%</p>
        </div>
      )}

      {isOffPlan && (
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <p className="text-xs text-purple-600 mb-1">Expected delivery</p>
          <p className="font-semibold text-purple-800">{fmtDate(p.expected_delivery_date)}</p>
        </div>
      )}
    </div>
  );
}

function AppreciationSection({ p }) {
  const gainPos = (p.appreciation || 0) >= 0;
  const dldLabel = p.dld_type === 'dubai' ? 'DLD fee on purchase (Dubai · 4%)'
    : p.dld_type === 'rak' ? 'DLD fee on handover (RAK · 4%)' : null;
  const totalInvestment = p.total_investment ?? p.purchase_price;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Purchase price</p>
          <p className="font-semibold">{money(p.purchase_price, p.currency)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-xs text-gray-500 mb-1">Current value</p>
          <p className="font-semibold">{money(p.current_value, p.currency)}</p>
        </div>
      </div>

      {/* Total cost breakdown */}
      <div className="bg-white rounded-xl p-4 border border-gray-100">
        <p className="text-sm font-semibold text-gray-700 mb-3">Total cost breakdown</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Purchase price</span>
            <span className="font-medium text-gray-800">{money(p.purchase_price, p.currency)}</span>
          </div>
          {dldLabel && (p.dld_fee || 0) > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">{dldLabel}</span>
              <span className="font-medium text-gray-800">{money(p.dld_fee, p.currency)}</span>
            </div>
          )}
          {(p.admin_fees || 0) > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Admin fees</span>
              <span className="font-medium text-gray-800">{money(p.admin_fees, p.currency)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-100">
            <span className="text-gray-700 font-semibold">Total investment</span>
            <span className="font-bold text-gray-900">{money(totalInvestment, p.currency)}</span>
          </div>
        </div>
      </div>
      <div className={`rounded-xl p-5 border ${gainPos ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
        <p className="text-xs text-gray-500 mb-1">Capital gain / loss</p>
        <p className={`text-2xl font-bold ${gainPos ? 'text-green-600' : 'text-red-600'}`}>
          {gainPos ? '+' : ''}{money(p.appreciation, p.currency)}
        </p>
        <p className={`text-sm font-medium ${gainPos ? 'text-green-600' : 'text-red-600'}`}>{p.appreciation_pct}% since purchase</p>
      </div>
      <p className="text-xs text-gray-400">Tip: use the ↻ button on the property card to update the current estimated value over time.</p>
    </div>
  );
}

function FilesSection({ p, headers, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [ftype, setFtype] = useState('brochure');

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('file_type', ftype);
    setUploading(true);
    try {
      await axios.post(`${API}/properties/${p.id}/files`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      await onChanged();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const remove = async (file) => {
    if (!window.confirm('Delete this file?')) return;
    await axios.delete(`${API}/properties/${p.id}/files/${file.id}`, { headers });
    await onChanged();
  };

  return (
    <div>
      <div className="bg-white rounded-xl p-4 border border-gray-100 mb-3">
        <label className="text-xs text-gray-500 mb-1 block">File type</label>
        <select value={ftype} onChange={e => setFtype(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-full mb-3">
          {FILE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="block">
          <span className="inline-block bg-green-600 text-white text-sm px-4 py-2 rounded-xl cursor-pointer">
            {uploading ? 'Uploading…' : '+ Upload file'}
          </span>
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>
        <p className="text-xs text-gray-400 mt-2">PDF, images, documents — max 20MB.</p>
      </div>

      {(!p.files || p.files.length === 0) ? (
        <p className="text-center text-gray-400 text-sm py-6">No files uploaded.</p>
      ) : (
        <div className="space-y-2">
          {p.files.map(f => (
            <div key={f.id} className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3">
              <span className="text-lg flex-shrink-0">📄</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{f.original_name}</p>
                <p className="text-xs text-gray-500 capitalize">{f.file_type} · {fmtDate(f.uploaded_at)}</p>
              </div>
              <a href={`https://affinityconcept.tech${f.url}`} target="_blank" rel="noreferrer"
                className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg flex-shrink-0">View</a>
              <button onClick={() => remove(f)} className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg flex-shrink-0">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
